<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-stepper v-model="stepNumber">
          <v-stepper-header>
            <v-stepper-step :complete="stepNumber > 1" step="1">
              File selection
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="stepNumber > 2" step="2">
              Content Mapping
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="stepNumber > 3" step="3">
              Result preview
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="stepNumber > 4" step="4">
              Import Summary
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-card elevation="0" max-width="450" class="mt-1">
                <v-col class="d-flex">
                  <v-file-input
                    show-size
                    accept="text/plain"
                    label="Select data file to upload"
                    v-model="fileToUpload"
                  ></v-file-input>
                  <v-btn class="align-self-center ml-3" @click="onUpload()"
                    ><v-icon>{{ icons.mdiUpload }}</v-icon
                    >Upload</v-btn
                  >
                </v-col>
              </v-card>
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-card elevation="0" class="mt-1">
                <v-row>
                  <v-col class="d-flex mb-6">
                    <v-btn @click="onBackToFileUpload()">File selection</v-btn>
                    <v-btn class="pa-2 ml-auto" @click="onReset()">Reset</v-btn>
                    <v-btn class="pa-2 ml-auto" @click="onPreview()">Preview</v-btn>
                  </v-col>
                </v-row>
                <v-row class="d-flex  justify-space-between ">
                  <v-card flat>
                    <v-card-title>Delimiter</v-card-title>
                    <v-card-text>
                      <v-chip-group
                        v-model="delimiter"
                        mandatory
                        column
                        @change="onDelimiterChange()"
                      >
                        <v-chip filter outlined>Comma ,</v-chip>
                        <v-chip filter outlined>Semicolon ;</v-chip>
                        <v-chip filter outlined>Colon :</v-chip>
                        <v-chip filter outlined>Pipe |</v-chip>
                      </v-chip-group>
                    </v-card-text>
                  </v-card>
                  <v-card flat>
                    <v-card-title>Quoted</v-card-title>
                    <v-card-text>
                      <v-chip-group
                        v-model="quoted"
                        mandatory
                        column
                        @change="onQuotedChange()"
                      >
                        <v-chip filter outlined>No quote</v-chip>
                        <v-chip filter outlined>Double quote "</v-chip>
                        <v-chip filter outlined>Single quote '</v-chip>
                      </v-chip-group>
                    </v-card-text>
                  </v-card>
                  <v-card flat>
                    <v-card-title>Lines to Skip</v-card-title>
                    <v-card-text>
                      <v-btn icon outlined class="mr-2" @click="onLineToSkipIncrement()"><v-icon>mdi-plus</v-icon></v-btn> 
                      <v-btn icon outlined class="mr-2" @click="onLineToSkipDecrement()"><v-icon>mdi-minus</v-icon></v-btn> 
                      <v-btn text disabled class="mr-2 text-h4"><span class="teal--text">{{linesToSkip}}</span></v-btn>   
                      
                    </v-card-text>
                  </v-card>                                          
                </v-row>                
                <v-col>
                  <v-data-table
                    :headers="headers"
                    :items="rows"
                    :items-per-page="20"
                    :hide-default-header="true"
                    class="elevation-1"
                  >
                    <template v-slot:header="{ props }">
                      <thead class="v-data-table-header">
                        <tr>
                          <th
                            role="columnheader"
                            scope="col"
                            class="text-start"
                            v-for="(hd, i) in props.headers"
                            :key="i"
                          >
                            <v-select
                              v-if="hd.value !== 'lineNumber'"
                              clearable
                              :items="columnTypes"
                              v-model="fieldTypes[hd.value]"
                            ></v-select>
                            <div class="d-flex">
                              <div>{{ hd.text }}</div>
                              <div class="ml-auto">
                                <v-icon v-if="hd.value !== 'lineNumber'" @click="onDeleteColumn(hd.value)"
                                  >mdi-close</v-icon
                                >
                              </div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                    </template>
                  </v-data-table>
                </v-col>
              </v-card>
            </v-stepper-content>
            <v-stepper-content step="3">
              <v-card elevation="0" class="mt-1">
                <v-row>
                  <v-col class="d-flex mb-6">
                    <v-btn @click="onBackToContentMapping()"
                      >Content Mapping</v-btn
                    >
                    <v-btn class="pa-2 ml-auto" @click="onImport()"
                      >Import All</v-btn
                    >
                  </v-col>
                </v-row>
                <h3>Result</h3>
                <v-row>
                  <v-col cols="12" md="4">
                    <item-list
                      v-if="processed.newAccounts.length > 0"
                      :title="'New Accounts (' + processed.newAccounts.length + ')'"
                      :items="processed.newAccounts"
                      itemText="fname"
                    />
                   
                  </v-col>
                  <v-col cols="12" md="4">
                     <item-list
                      v-if="processed.newCounterparties.length > 0"
                      :title="'New Counterparties (' + processed.newCounterparties.length + ')'"
                      :items="processed.newCounterparties"
                      itemText="name"
                    />
                  </v-col>
                  <v-col cols="12" v-if="processed.invalidRows.length > 0">
                    <h4>
                      Invalid entries
                      {{ "(" + processed.invalidRows.length + ")" }}
                    </h4>
                    <v-data-table
                      :headers="headers"
                      :items="processed.invalidRows"
                      :items-per-page="20"
                      :hide-default-header="true"
                      class="elevation-1"
                    >
                      <template v-slot:header="{ props }">
                        <thead class="v-data-table-header">
                          <tr>
                            <th
                              role="columnheader"
                              scope="col"
                              class="text-start"
                              v-for="(hd, i) in props.headers"
                              :key="i"
                            >
                              <div class="d-flex">
                                <div>{{ fieldTypes[hd.value] ? fieldTypes[hd.value] : hd.text }}</div>
                              </div>
                            </th>
                          </tr>
                        </thead>
                      </template>
                      <template v-slot:item.errors="{ item }">
                        <v-chip
                          class="mr-2"
                          color="red"
                          dark
                          v-for="(error, i) in item.errors" :key="i"
                        >
                          {{ error }}
                        </v-chip>
                      </template>
                    </v-data-table>
                  </v-col>
                  <v-col cols="12" v-if="processed.transactions.length > 0">
                    <h4>
                      New Transactions
                      {{ "(" + processed.transactions.length + ")" }}
                    </h4>
                    <v-data-table
                      :headers="newTransactionPreviewHeaders"
                      :items="processed.transactions"
                      :items-per-page="10"
                      item-key="uuid"
                      class="elevation-1"
                      dense
                    >
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-card>
            </v-stepper-content>
            <v-stepper-content step="4">
              <v-card elevation="0" max-width="450" class="mt-1">
                <v-row>
                  <v-col class="d-flex mb-6">
                    <v-btn class="pa-2" @click="onFinish()">Finish</v-btn>
                  </v-col>
                </v-row>
                <h3>Summary</h3>
                <h4 v-if="processed.newAccounts.length > 0">{{ processed.newAccounts.length }} accounts imported</h4>
                <h4 v-if="processed.newCounterparties.length > 0">{{ processed.newCounterparties.length }} counterparties imported</h4>
                <h4 v-if="processed.transactions.length > 0">{{ processed.transactions.length }} transactions imported</h4>
              </v-card>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-col>
    </v-row>
    <progress-dialog :show="dialog.show" :message="dialog.message" :mode="dialog.mode" @onClose="hideDialog()"/>
  </v-container>
</template>

<script>
import { mdiUpload } from "@mdi/js";
import {
  ColumnTypes,
  CustomizedFormat,
  DelimitedTextFile,
  ImportProcessor,
} from "@/model";
import ItemList from "./common/ItemList.vue";
import ProgressDialog from "./common/ProgressDialog.vue";

const AVAILABLE_DELIMITERS = [",", ";", ":", "|"];
const AVAILABLE_QUOTES = ["", '"', "'"];

const initState = () => ({
  icons: {
    mdiUpload,
  },
  stepNumber: 1,

  fileToUpload: undefined,
  
  delimiter: [],
  quoted: [],
  linesToSkip: 0,
  
  columnTypes: [...ColumnTypes],
  fieldTypes: {},
  headers: [],
  rows: [],
  delimitedFile: undefined,

  processed: {
    invalidRows: [],
    newAccounts: [],
    newCounterparties: [],
    transactions: [],
  },

  newEntities: undefined,
  newTransactionPreviewHeaders: [
    { text: "Date", value: "date" },
    { text: "Counterparty", value: "counterparty" },
    { text: "Amount", value: "amount", align: "end" },
  ],
  
  dialog: {
    show: false,
    message: "",
    mode: ""
  },
});

export default {
  name: "Import",
  components: {
    ItemList,
    ProgressDialog,
  },
  data: () => initState(),
  async created() {
    await this.$store.dispatch("getPrimaryCurrency");
    await this.$store.dispatch("getAccounts");
    await this.$store.dispatch("getCounterparties");
  },
  computed: {
    primaryCurrency() {
      return this.$store.state.primaryCurrency;
    },
    allAccounts() {
      return this.$store.state.accounts;
    },
    allCounterparties() {
      return this.$store.state.counterparties;
    },
  },
  methods: {
    async onLineToSkipIncrement() {
      this.linesToSkip++;
      await this.onUpload();
    },
    async onLineToSkipDecrement() {
      if (this.linesToSkip > 0 ) {
        this.linesToSkip--;
        await this.onUpload();
      }
    },
    async onDelimiterChange() {
      await this.onUpload();
    },
    async onQuotedChange() {
      await this.onUpload();
    },
    initializeHeaders(nbCols) {
      this.headers = [{ text: "#", value: "lineNumber" }];
      for (let i = 0; i < nbCols; i++) {
        const index = i.toString();
        const hIndex = (i + 1).toString();
        this.headers.push({ text: "Column " + hIndex, value: index });
        this.fieldTypes[index] = "";
      }
    },
    onDeleteColumn(value) {
      const hIndex = this.headers.findIndex((h) => h.value === value);
      if (hIndex) {
        this.headers.splice(hIndex, 1);
      }
      this.rows.forEach((row) => {
        if (value in row) {
          delete row[value];
        }
      });
    },
    async onUpload() {
      if (!this.fileToUpload) {
        return;
      }
      const delimiter = AVAILABLE_DELIMITERS[this.delimiter];
      const quoted = AVAILABLE_QUOTES[this.quoted];
      const delimitedFile = new DelimitedTextFile(this.fileToUpload, delimiter, this.linesToSkip, quoted);
      await delimitedFile.readContent();
      const rows = delimitedFile.getRows()
        .map(row => row.object);
      this.rows = rows;
      const nbCols = delimitedFile.getRows().map(row => row.length).reduce((a,b) => a > b ? a : b, 0);
      this.initializeHeaders(nbCols);

      this.fieldTypes= {};
      this.delimitedFile = delimitedFile;
      this.stepNumber = 2;
    },
    onBackToFileUpload() {
      Object.assign(this, initState());
      this.stepNumber = 1;
    },
    validateFormat(format) {
      const nonMappedTypes = ColumnTypes.filter(type => {
        try {
          format.getColumnIndex(type)
          return false;
        } catch (e) {
          return true;
        }
      });
      if (nonMappedTypes.length > 0 ) {
        const msg = "No column selected for type(s) : " + nonMappedTypes.join(", "); 
        this.showInfoDialog(msg);
        return false;
      }
      return true;
    },
    onReset() {
        this.delimiter = [],
        this.quoted = [];
        this.linesToSkip = 0;
        this.columnTypes = [...ColumnTypes];
        this.fieldTypes = {};
    },
    onPreview() {
      const format = new CustomizedFormat(this.fieldTypes);
      if (!this.validateFormat(format)) {
        return;
      }

      const processor = new ImportProcessor(
        this.delimitedFile.getRows(),
        format,
        this.allAccounts,
        this.allCounterparties,
        this.primaryCurrency
      );
      processor.process();
      const newEntities = processor.getNewEntities();

      const invalidRows = processor.getInvalidRows().map((parsedRow) => {
        const obj = Object.assign(parsedRow.row.object, obj);
        obj["errors"] = parsedRow.errors;
        return obj;
      });
      this.processed = {
        invalidRows: invalidRows,
        newAccounts: newEntities.accounts,
        newCounterparties: newEntities.counterparties,
        transactions: newEntities.transactions
          .map((tx) => tx.getBrief())
          .map((txB) => ({
            uuid: txB.uuid,
            date: this.$options.filters.formatDate(txB.date),
            counterparty: txB.counterparty,
            amount: this.$options.filters.formatAmount(txB.amount),
          })),
      };
      this.addErrorColumn();
      this.newEntities = newEntities;
      this.stepNumber = 3;
    },
    addErrorColumn() {
      this.headers.push({ text: 'Error', value: 'errors'});
      this.fieldTypes["errors"] = "Error";
    },
    removeErrorColumn() {
      const index = this.headers.findIndex(hd => hd.value === "errors");
      this.headers.splice(index, 1);
      delete this.fieldTypes["errors"];
    },
    onBackToContentMapping() {
      this.stepNumber = 2;
      this.removeErrorColumn();
    },
    async onImport() {
      if (!this.newEntities) {
        throw Error("Illegal state");
      }
      this.showImportProgressDialog();  
      await this.$store.dispatch("saveEntityContainer", this.newEntities);
      this.hideDialog();
      this.stepNumber = 4;
    },
    showInfoDialog(msg) {
      this.dialog = {
        mode: "info",
        message: msg,
        show: true,
      };
    },
    showImportProgressDialog() {
      this.dialog = {
        mode: "progress",
        message: "Import in progress",
        show: true,
      };
    },
    hideDialog() {
      this.dialog = {
        mode: "",
        message: "",
        show: false,
      };
    },
    onFinish() {
      Object.assign(this, initState());
    },
    onPreviousToUpload() {
      this.delimitedFile = undefined;
      this.rows = [];
      this.stepNumber = 1;
    },
  },
};
</script>